import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { quality, qualitySection } from '@/data/content';

const COLORS = [
    {
        card: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200/60 dark:border-orange-800/40',
        icon: 'bg-app-primary/15 text-app-primary',
    },
    {
        card: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200/60 dark:border-blue-800/40',
        icon: 'bg-blue-500/15 text-blue-500',
    },
    {
        card: 'bg-green-50 dark:bg-green-900/20 border-green-200/60 dark:border-green-800/40',
        icon: 'bg-green-500/15 text-green-600',
    },
    {
        card: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200/60 dark:border-purple-800/40',
        icon: 'bg-purple-500/15 text-purple-500',
    },
];

const QualitySection: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={ref} className="py-20 md:py-32 bg-gray-50 dark:bg-gray-900/40 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className="text-center mb-16 md:mb-20"
                    style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'translateY(0)' : 'translateY(24px)',
                        transition: 'opacity 600ms ease, transform 600ms ease',
                    }}
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-app-primary/10 text-app-primary text-xs font-black uppercase tracking-widest mb-4">
                        {qualitySection.badge}
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white leading-tight">
                        {qualitySection.heading}
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {quality.map((item, i) => {
                        const c = COLORS[i % COLORS.length];
                        return (
                            <div
                                key={item.title}
                                className={cn('flex flex-col items-center text-center p-10 rounded-[2.5rem] border', c.card, 'shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-default group')}
                                style={{
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.96)',
                                    transition: `opacity 800ms ease, transform 800ms ease, background 500ms ease, border 500ms ease, box-shadow 500ms ease`,
                                    transitionDelay: `${i * 130}ms`,
                                }}
                            >
                                <div
                                    className={cn('w-20 h-20 rounded-3xl', c.icon, 'flex items-center justify-center text-4xl mb-6 shadow-sm group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500')}
                                    style={{
                                        transition: `transform 500ms ease ${i * 130 + 300}ms`,
                                        transform: visible ? 'scale(1)' : 'scale(0.6)',
                                    }}
                                >
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-3">{item.title}</h3>
                                <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                                    {item.description}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default QualitySection;
